import { create } from 'zustand';
import { can, type PermissionKey, type Role } from '@dd/shared';
import { api, ApiError, getAccessToken, refreshFailure, refreshSession, setAccessToken, type ProfileDto, type SessionDto } from '@/lib/api';
import { closeShopDb, db, deviceId, metaGet, metaSet, openShopDb, requestPersistence } from '@/db';
import { forgetSession, knownSessions, lastKnownSession, rememberSession, verifyOffline, type KnownSession } from '@/lib/offlineAuth';
import { engine } from '@/sync/engine';
import { setSyncContext } from '@/sync/context';
import { setSync } from '@/sync/status';
import { useLang } from '@/lib/i18n';

export type Phase = 'boot' | 'out' | 'locked' | 'ready';

interface SessionState {
  phase: Phase;
  profile: ProfileDto | null;
  deviceCode: string | null;
  deviceId: string;
  known: KnownSession | null;
  allKnown: KnownSession[];
  /** offline-only session (PIN verified locally, no server token yet) */
  offline: boolean;
  boot: () => Promise<void>;
  establish: (dto: SessionDto, pin?: string | null) => Promise<void>;
  unlockOffline: (pin: string) => Promise<boolean>;
  logout: (opts?: { forget?: boolean }) => Promise<void>;
  can: (perm: PermissionKey) => boolean;
}

export const useSession = create<SessionState>((set, get) => ({
  phase: 'boot', profile: null, deviceCode: null, deviceId: '', known: null, allKnown: [], offline: false,

  async boot() {
    const did = await deviceId();
    const known = await lastKnownSession();
    const allKnown = await knownSessions();
    set({ deviceId: did, known, allKnown });
    if (!known) return set({ phase: 'out' });
    const s = await refreshSession();
    if (s) return get().establish(s, null);
    if (refreshFailure === 'network' && known.profile.shopId) return set({ phase: 'locked', known });
    set({ phase: 'out', known });
  },

  async establish(dto, pin) {
    setAccessToken(dto.accessToken, dto.expiresIn);
    const p = dto.profile;
    const did = get().deviceId || (await deviceId());
    useLang.getState().setLang(p.language);
    if (p.shopId) {
      openShopDb(p.shopId, p.role);
      const dc = dto.deviceCode ?? (await metaGet<string>('deviceCode')) ?? null;
      if (dc) await metaSet('deviceCode', dc);
      setSyncContext({ shopId: p.shopId, deviceId: did, deviceCode: dc, role: p.role, actor: { type: p.principalType, id: p.principalId }, accountId: p.accountId });
      const settings = await db().settings.toCollection().first();
      await rememberSession(p, dc, pin ?? null, { shopName: settings?.name, category: settings?.category });
      void requestPersistence();
      set({ profile: p, deviceCode: dc, phase: 'ready', offline: false, deviceId: did });
      engine.stop();
      await engine.start();
      engine.resumeAfterLogin();
    } else {
      closeShopDb();
      setSyncContext(null);
      await rememberSession(p, null, pin ?? null);
      set({ profile: p, deviceCode: null, phase: 'ready', offline: false, deviceId: did });
    }
    set({ allKnown: await knownSessions(), known: await lastKnownSession() });
  },

  async unlockOffline(pin) {
    const k = get().known;
    if (!k || !k.profile.shopId) return false;
    if (!(await verifyOffline(k, pin))) return false;
    const did = get().deviceId || (await deviceId());
    openShopDb(k.profile.shopId, k.profile.role);
    setSyncContext({ shopId: k.profile.shopId, deviceId: did, deviceCode: k.deviceCode, role: k.profile.role, actor: { type: k.profile.principalType, id: k.profile.principalId }, accountId: k.profile.accountId });
    useLang.getState().setLang(k.profile.language);
    set({ profile: k.profile, deviceCode: k.deviceCode, phase: 'ready', offline: true });
    await engine.start();
    // the moment the network is back the engine refreshes the token through the cookie; if that fails the app asks for a normal sign-in
    return true;
  },

  async logout(opts) {
    const { profile } = get();
    try { await api.post('/auth/logout'); } catch { /* offline: the cookie expires on its own */ }
    engine.stop();
    setAccessToken(null);
    setSyncContext(null);
    setSync({ pending: 0, failed: 0, quarantined: 0 });
    if (opts?.forget && profile) await forgetSession(profile.principalId, profile.shopId);
    closeShopDb();
    set({ phase: 'out', profile: null, deviceCode: null, known: await lastKnownSession(), allKnown: await knownSessions() });
  },

  can(perm) {
    const r = get().profile?.role;
    return r ? can(r as Role, perm) : false;
  },
}));

export const useRole = (): Role => useSession((s) => (s.profile?.role ?? 'cashier') as Role);
export const useCan = (perm: PermissionKey): boolean => useSession((s) => (s.profile ? can(s.profile.role as Role, perm) : false));
export const isAuthed = (): boolean => !!getAccessToken();
export { ApiError };
